// components/RoutePolygonTable.jsx
'use client';
import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MenuIcon from '@mui/icons-material/Menu';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { usePoolList } from '../../../stores/poolStore';
import { useTransitPointList } from '../../../stores/transitPointStore';
import { useGeofenceList } from '../../../stores/geofenceStore';
import { useLanguage } from '../../../utils/i18n';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { baseMaps } from '../../../utils/mapConfig';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const RouteMapPreview = ({ points, activeFocusId }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  const pointsString = useMemo(() => {
    return JSON.stringify(points.filter((p) => p.lokasi).map((p) => p.lokasi.realId));
  }, [points]);

  useEffect(() => {
    if (mapRef.current) return;
    if (!containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [-2.5489, 118.0148],
      zoom: 5,
      zoomControl: true,
      attributionControl: false,
      layers: [baseMaps['OpenStreetMap'] || Object.values(baseMaps)[0]]
    });

    L.control.layers(baseMaps, null, { position: 'topright' }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.eachLayer((layer) => {
      if (layer instanceof L.Polygon || layer instanceof L.Polyline || layer instanceof L.Marker || layer instanceof L.CircleMarker) {
        map.removeLayer(layer);
      }
    });

    const validPoints = points.filter((p) => p.lokasi && p.lokasi.lat != null && p.lokasi.lon != null);
    if (validPoints.length === 0) return;

    const latLngs = [];

    validPoints.forEach((point, index) => {
      const loc = point.lokasi;
      let markerLatLng = [Number(loc.lat), Number(loc.lon)];

      if (loc.geom) {
        try {
          const wkt = loc.geom;
          const coordsStr = wkt.replace('POLYGON((', '').replace('))', '').replace('POLYGON ((', '');
          const pairs = coordsStr.split(',');
          const polyCoords = pairs.map((pair) => {
            const [lng, lat] = pair.trim().split(' ');
            return [Number(lat), Number(lng)];
          });

          if (polyCoords.length > 0) {
            const polygon = L.polygon(polyCoords, { color: '#00279c', weight: 2, fillOpacity: 0.2 }).addTo(map);
            const center = polygon.getBounds().getCenter();
            markerLatLng = [center.lat, center.lng];
            point._calculatedCenter = markerLatLng;
          }
        } catch (e) {
          console.warn('Failed to parse WKT for map preview', e);
        }
      } else {
        point._calculatedCenter = markerLatLng;
      }

      latLngs.push(markerLatLng);

      const markerColor = index === 0 ? '#4CAF50' : index === validPoints.length - 1 ? '#F44336' : '#2196F3';
      const numberedIcon = L.divIcon({
        className: 'custom-number-marker',
        html: `
          <div style="
            background-color: ${markerColor}; color: white; width: 24px; height: 24px;
            border-radius: 50%; border: 2px solid white; display: flex; align-items: center;
            justify-content: center; font-weight: bold; font-size: 12px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3); transition: transform 0.3s ease;
            ${point.id === activeFocusId ? 'transform: scale(1.3); border-color: #FFEB3B; box-shadow: 0 0 10px rgba(255,235,59,0.8);' : ''}
          ">
            ${index + 1}
          </div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });

      L.marker(markerLatLng, { icon: numberedIcon })
        .bindPopup(`<b>Urutan ${index + 1}</b><br/>${loc.label}<br/>(${loc.type})`)
        .addTo(map);
    });

    if (latLngs.length > 1) {
      L.polyline(latLngs, { color: '#005ff8', weight: 3, dashArray: '8, 8' }).addTo(map);
    }

    if (latLngs.length > 0 && !activeFocusId) {
      const bounds = L.latLngBounds(latLngs);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [points, pointsString, activeFocusId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !activeFocusId) return;

    const focusPoint = points.find((p) => p.id === activeFocusId);
    if (focusPoint && focusPoint._calculatedCenter) {
      map.flyTo(focusPoint._calculatedCenter, 15, { animate: true, duration: 1.5 });
    }
  }, [activeFocusId, points]);

  return <Box ref={containerRef} sx={{ width: '100%', height: 350, borderRadius: 2, border: '1px solid #e0e0e0', zIndex: 0 }} />;
};

export default function RoutePolygonTable({ routes, setRoutes, isReadOnly = false, companyId = '' }) {
  const { t } = useLanguage();
  const [globalSearch, setGlobalSearch] = useState('');
  const debouncedSearch = useDebounce(globalSearch, 500);
  const [activeFocusId, setActiveFocusId] = useState(null);

  const { data: rawPools, isLoading: loadPool } = usePoolList(0, 50, 'name', debouncedSearch, companyId);
  const { data: rawTransitPoints, isLoading: loadTransit } = useTransitPointList(0, 50, 'name', debouncedSearch, companyId);
  const { data: rawGeofences, isLoading: loadGeo } = useGeofenceList(0, 50, 'name', debouncedSearch, companyId);

  const locationOptions = useMemo(() => {
    let options = [];

    if (rawPools) {
      options = [
        ...options,
        ...rawPools.map((p) => ({
          id: `pool-${p.id}`,
          realId: p.id,
          label: p.name || p.pool_name,
          type: 'pool',
          lat: p.centroid_lat ?? p.latitude,
          lon: p.centroid_lon ?? p.longitude,
          geom: p.geom
        }))
      ];
    }

    if (rawTransitPoints) {
      const tpList = Array.isArray(rawTransitPoints) ? rawTransitPoints : rawTransitPoints?.data || [];
      options = [
        ...options,
        ...tpList.map((tp) => ({
          id: `tp-${tp.id}`,
          realId: tp.id,
          label: tp.name || tp.transit_point_name,
          type: 'transit_point',
          lat: tp.centroid_lat ?? tp.latitude,
          lon: tp.centroid_lon ?? tp.longitude,
          geom: tp.geom
        }))
      ];
    }

    if (rawGeofences) {
      const geoList = Array.isArray(rawGeofences) ? rawGeofences : rawGeofences?.data || [];
      options = [
        ...options,
        ...geoList.map((g) => ({
          id: `geo-${g.id}`,
          realId: g.id,
          label: g.name || g.geofence_name,
          type: 'geofence',
          lat: g.centroid_lat ?? g.latitude,
          lon: g.centroid_lon ?? g.longitude,
          geom: g.geom
        }))
      ];
    }

    return options;
  }, [rawPools, rawTransitPoints, rawGeofences]);

  const isSearching = loadPool || loadTransit || loadGeo;

  // KEY LOGIC: Menambal data routes yang miskin info (dari mode Edit) dengan data penuh dari locationOptions
  useEffect(() => {
    if (!routes || routes.length === 0 || locationOptions.length === 0) return;

    let needsUpdate = false;
    const patchedRoutes = routes.map((route) => {
      if (route.lokasi && route.lokasi.realId && !route.lokasi.geom && route.lokasi.lat == null) {
        // Data dari API Edit Mode biasanya tidak punya geom/lat/lon
        // Cari padanannya di locationOptions yang sudah lengkap dengan koordinat
        const fullLocationData = locationOptions.find(
          (opt) => String(opt.realId) === String(route.lokasi.realId) && opt.type === route.lokasi.type
        );

        if (fullLocationData) {
          needsUpdate = true;
          return { ...route, lokasi: fullLocationData };
        }
      }
      return route;
    });

    if (needsUpdate) {
      setRoutes(patchedRoutes);
    }
  }, [routes, locationOptions, setRoutes]);

  const handleAddRoute = () => {
    if (isReadOnly) return;
    const newId = Date.now();
    setRoutes([...routes, { id: newId, lokasi: null }]);
    setActiveFocusId(newId);
  };

  const handleRemoveRoute = (id) => {
    if (isReadOnly) return;
    setRoutes(routes.filter((r) => r.id !== id));
    if (activeFocusId === id) setActiveFocusId(null);
  };

  const handleChange = (id, value) => {
    if (isReadOnly) return;
    const updated = routes.map((r) => {
      if (r.id === id) {
        return { ...r, lokasi: value };
      }
      return r;
    });
    setRoutes(updated);
    setGlobalSearch('');
    setActiveFocusId(id);
  };

  const handleInputChange = (newInputValue, reason) => {
    if (isReadOnly) return;
    if (reason === 'input') setGlobalSearch(newInputValue);
    else if (reason === 'clear' || reason === 'reset') setGlobalSearch('');
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const reorderedRoutes = Array.from(routes);
    const [movedItem] = reorderedRoutes.splice(sourceIndex, 1);
    reorderedRoutes.splice(destinationIndex, 0, movedItem);
    setRoutes(reorderedRoutes);
    setActiveFocusId(null);
  };

  const getChipProps = (type) => {
    if (type === 'pool') return { label: 'Pool', bgcolor: '#e3f2fd', color: '#1565c0' };
    if (type === 'geofence') return { label: 'Geofence', bgcolor: '#f3e5f5', color: '#7b1fa2' };
    if (type === 'transit_point') return { label: 'Transit Pt', bgcolor: '#e8f5e9', color: '#2e7d32' };
    return { label: type || '-', bgcolor: '#f5f5f5', color: '#616161' };
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#485D9A', mb: 1 }}>
          {t('masterRoute.routeMapVisualization', 'Route Map Visualization')}
        </Typography>
        <RouteMapPreview points={routes} activeFocusId={activeFocusId} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#485D9A' }}>
            {t('masterRoute.routePolygons', 'Route Polygons')}
          </Typography>
          <Typography sx={{ color: '#6b7280', fontSize: 11 }}>
            {t('masterRoute.routePolygonsTips', 'Specify the sequence of areas to pass. Type name to search.')}
          </Typography>
        </Box>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mb: 2 }}>
          <Table sx={{ minWidth: 600 }} size="small">
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell width="40px" align="center">
                  {t('masterRoute.no', 'No')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>{t('masterRoute.locationPolygon', 'Location / Polygon')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#4b5563' }} width="150px">
                  {t('masterRoute.type', 'Type')}
                </TableCell>
                <TableCell width="50px" align="center"></TableCell>
              </TableRow>
            </TableHead>

            <Droppable droppableId="polygon-table-body" isDropDisabled={isReadOnly}>
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {routes.map((route, index) => {
                    // Safe parsing for Autocomplete value to prevent unmounted/undefined errors
                    let autocompleteValue = null;
                    if (route.lokasi && route.lokasi.realId) {
                      // Selalu cari reference asli dari options berdasarkan RealId dan Type agar Material UI tidak complain
                      const matchedOption = locationOptions.find(
                        (opt) => String(opt.realId) === String(route.lokasi.realId) && opt.type === route.lokasi.type
                      );
                      autocompleteValue = matchedOption || route.lokasi;
                    }

                    return (
                      <Draggable key={String(route.id)} draggableId={String(route.id)} index={index}>
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              bgcolor: activeFocusId === route.id ? 'rgba(72, 93, 154, 0.05)' : 'inherit',
                              transition: 'background-color 0.3s ease',
                              ...(snapshot.isDragging && {
                                display: 'table',
                                backgroundColor: '#f3f4f6',
                                boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
                                zIndex: 1000
                              }),
                              '&:last-child td, &:last-child th': { border: 0 }
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{ color: '#9ca3af', cursor: 'pointer', '&:hover': { color: '#485D9A' } }}
                              onClick={() => setActiveFocusId(route.id)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <div
                                  {...provided.dragHandleProps}
                                  style={{ display: 'inline-flex', cursor: isReadOnly ? 'not-allowed' : 'grab' }}
                                >
                                  <MenuIcon fontSize="small" />
                                </div>
                                <Typography variant="caption" fontWeight="bold">
                                  {index + 1}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={locationOptions}
                                disabled={isReadOnly}
                                getOptionLabel={(option) => option.label || ''}
                                isOptionEqualToValue={(option, value) =>
                                  String(option.realId) === String(value.realId) && option.type === value.type
                                }
                                value={autocompleteValue}
                                onChange={(event, newValue) => handleChange(route.id, newValue)}
                                onInputChange={(event, newInputValue, reason) => handleInputChange(newInputValue, reason)}
                                filterOptions={(x) => x}
                                noOptionsText={
                                  isSearching
                                    ? t('masterRoute.searching', 'Searching...')
                                    : t('masterRoute.typeMin2Chars', 'Type min. 2 chars...')
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={t('masterRoute.searchPoolGeoTransit', 'Search Pool / Geofence / Transit Pt...')}
                                    sx={{ bgcolor: isReadOnly ? '#f9fafb' : '#fff' }}
                                    onFocus={() => setActiveFocusId(route.id)}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {isSearching ? <CircularProgress color="inherit" size={16} /> : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      )
                                    }}
                                  />
                                )}
                                renderOption={(props, option) => {
                                  const chipProps = getChipProps(option.type);
                                  return (
                                    <li {...props} key={option.id}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="body2">{option.label}</Typography>
                                        <Typography variant="caption" sx={{ color: chipProps.color }}>
                                          {chipProps.label}
                                        </Typography>
                                      </Box>
                                    </li>
                                  );
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              {route.lokasi ? (
                                <Tooltip title={getChipProps(route.lokasi.type).label} placement="top" arrow>
                                  <Chip
                                    icon={<InfoOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                                    label={getChipProps(route.lokasi.type).label}
                                    size="small"
                                    sx={{
                                      bgcolor: getChipProps(route.lokasi.type).bgcolor,
                                      color: getChipProps(route.lokasi.type).color,
                                      fontWeight: 'bold',
                                      fontSize: '0.7rem',
                                      cursor: 'help'
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                '-'
                              )}
                            </TableCell>

                            <TableCell align="center">
                              <IconButton color="error" onClick={() => handleRemoveRoute(route.id)} disabled={isReadOnly}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
      </DragDropContext>

      {!isReadOnly && (
        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddRoute}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', bgcolor: '#f9fafb' }}
        >
          {t('masterRoute.addPolygon', 'Add Polygon')}
        </Button>
      )}
    </Box>
  );
}
